import { Button, Select } from '@ludus/ui'
import { useTranslation } from 'react-i18next'
import { addDays, formatWeekRange, startOfISOWeek } from '../../utils/weekRange.ts'

interface WeekToolbarProps {
  visibleDate: Date
  onChangeWeek: (d: Date) => void
  onCreate: () => void
  canCreate: boolean
}

export function WeekToolbar({ visibleDate, onChangeWeek, onCreate, canCreate }: WeekToolbarProps) {
  const { t } = useTranslation()
  const weekStart = startOfISOWeek(visibleDate)

  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border/60 bg-background-app px-4 py-2.5">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChangeWeek(new Date())}
        >
          {t('pages.calendar.toolbar.today')}
        </Button>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('pages.calendar.toolbar.prev')}
            onClick={() => onChangeWeek(addDays(weekStart, -7))}
          >
            <ChevronIcon dir="left" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('pages.calendar.toolbar.next')}
            onClick={() => onChangeWeek(addDays(weekStart, 7))}
          >
            <ChevronIcon dir="right" />
          </Button>
        </div>
        <h2 className="ml-2 text-base font-semibold text-foreground">
          {formatWeekRange(weekStart)}
        </h2>
      </div>

      <div className="flex items-center gap-2">
        <Select
          value="week"
          onValueChange={() => {
            /* only Week supported for now */
          }}
          options={[{ value: 'week', label: t('pages.calendar.toolbar.viewWeek') }]}
        />
        <Button type="button" variant="primary" size="sm" disabled={!canCreate} onClick={onCreate}>
          <PlusIcon />
          <span>{t('pages.calendar.toolbar.create')}</span>
        </Button>
      </div>
    </div>
  )
}

function ChevronIcon({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {dir === 'left' ? (
        <polyline points="15 18 9 12 15 6" />
      ) : (
        <polyline points="9 18 15 12 9 6" />
      )}
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}
